import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Row, Col, Card, Table, Button, Input, Select, DatePicker,
  notification, Tag, Spin, Typography, Tooltip, Avatar, Checkbox,
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import { usePaymentDropdowns } from "../../hooks/usePaymentDropdowns";
import { SearchOutlined, FileExcelOutlined, WarningOutlined, UserOutlined } from "@ant-design/icons";
import moment from "moment";
import * as XLSX from "xlsx";
import userDummy from "../../assets/images/dummy-user.png";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const DEFAULT_THRESHOLD = 50000;

// These values must match exactly what is stored in sn_payments.pyament_mode_type
const PAYMENT_MODES = [
  { label: "UPI",                    value: "UPI"                    },
  { label: "Cash",                   value: "Cash"                   },
  { label: "Cheque",                 value: "Cheque"                 },
  { label: "RTGS/NEFT",             value: "RTGS-NEFT"              },
  { label: "Website-Razorpay",       value: "Website-Razorpay"       },
  { label: "Application-Razorpay",   value: "Application-Razorpay"   },
  { label: "Member Portal-Rozapay",  value: "Member-Portal-Razorpay" },
  { label: "Registration-Rozapay",   value: "Registration-Rozapay"   },
];

// langVariant = true  → field has _en / _hn versions (use language toggle)
// txnLevel    = true  → field lives on transactions, not on member object
const OPTIONAL_COLUMNS = [
  { label: "Father Name",  key: "father_name",              langVariant: true  },
  { label: "Village",      key: "per_village",              langVariant: true  },
  { label: "Tehsil",       key: "per_tahsil",               langVariant: true  },
  { label: "District",     key: "per_district",             langVariant: true  },
  { label: "State",        key: "per_state"                                    },
  { label: "Pincode",      key: "per_pincode"                                  },
  { label: "Joining Date", key: "joining_date"                                 },
  { label: "Account",      key: "bank_account",             txnLevel: true     },
  { label: "Mode",         key: "pyament_mode_type",        txnLevel: true     },
  { label: "Occasion",     key: "special_occassion_category", txnLevel: true   },
  { label: "Remarks",      key: "remarks",                  txnLevel: true     },
  { label: "Status",       key: "payment_virify",           txnLevel: true     },
  { label: "Aadhar No",    key: "aadhar_no"                                    },
];

function TransactionSummaryReport() {
  const { bankAccounts } = usePaymentDropdowns();

  const [loading, setLoading]                   = useState(false);
  const [groupedData, setGroupedData]           = useState([]);
  const [grandTotal, setGrandTotal]             = useState(0);
  const [aboveThresholdCount, setAboveThresholdCount] = useState(0);
  const [threshold, setThreshold]               = useState(DEFAULT_THRESHOLD);
  const [language, setLanguage]                 = useState("en"); // "en" | "hi"

  const [visibleCols, setVisibleCols] = useState({
    father_name:                false,
    per_village:                false,
    per_tahsil:                 false,
    per_district:               false,
    per_state:                  false,
    per_pincode:                false,
    joining_date:               false,
    bank_account:               false,
    pyament_mode_type:          false,
    special_occassion_category: false,
    remarks:                    false,
    payment_virify:             false,
    aadhar_no:                  false,
  });

  const [filters, setFilters] = useState({
    txn_date_range:     [],
    bank_account:       "",
    pyament_mode_type:  "",
    name:               "",
    phone:              "",
    amountMin:          "",
    payment_virify:     "",
  });

  // Returns the language-appropriate value for a langVariant field
  const langVal = (member, baseKey) => {
    if (language === "hi") return member[baseKey + "_hn"] || member[baseKey] || "—";
    return member[baseKey + "_en"] || member[baseKey] || "—";
  };

  // Collects unique non-empty values from a member's transactions for a given key
  const txnUniqueVals = (member, key) => {
    const vals = [...new Set(
      member.transactions.map(t => t[key]).filter(v => v && v !== "—")
    )];
    return vals.join(", ") || "—";
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const toggleCol = (key, checked) => {
    setVisibleCols(prev => ({ ...prev, [key]: checked }));
  };

  const buildQueryParams = () => {
    const params = {};
    if (filters.txn_date_range && filters.txn_date_range.length === 2)
      params.txn_date_range = filters.txn_date_range.join(",");
    if (filters.bank_account)      params.bank_account      = filters.bank_account;
    if (filters.pyament_mode_type) params.pyament_mode_type = filters.pyament_mode_type;
    if (filters.name)              params.name              = filters.name;
    if (filters.phone)             params.phone             = filters.phone;
    if (filters.payment_virify)    params.payment_virify    = filters.payment_virify;
    return Object.keys(params).map(k => `${k}=${params[k]}`).join("&");
  };

  const fetchReport = async () => {
    if (!filters.txn_date_range || filters.txn_date_range.length !== 2) {
      notification.warning({
        message: "Date Range Required",
        description: "Please select a Transaction Date Range to generate the report.",
        placement: "topRight",
      });
      return;
    }
    setLoading(true);
    try {
      const qp = buildQueryParams();
      const response = await axios.get(
        `${baseUrl}/payments/list?page=1&limit=all&${qp}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } }
      );
      if (response.data.status) {
        const payments = response.data.result || [];
        const grouped = groupByMember(payments);
        setGroupedData(grouped);
        const gt = grouped.reduce((s, m) => s + m.totalPaid, 0);
        setGrandTotal(gt);
        setAboveThresholdCount(grouped.filter(m => m.totalPaid >= threshold).length);
      } else {
        notification.info({ message: "Info", description: response.data.message, placement: "topRight" });
      }
    } catch {
      notification.error({ message: "Error", description: "Failed to fetch report data.", placement: "topRight" });
    } finally {
      setLoading(false);
    }
  };

  const groupByMember = (payments) => {
    const map = {};
    payments.forEach(p => {
      const id = p.mamber_id || p.user_id || p.phone_no;
      if (!map[id]) {
        map[id] = {
          mamber_id:       p.mamber_id || p.user_id,
          // Name — API returns name_eng (EN) and name_hn (HN)
          name:            p.name || p.name_hn || p.name_eng || "—",
          name_eng:        p.name_eng || p.name || "—",
          name_hn:         p.name_hn  || p.name || "—",
          phone_no:        p.phone_no || "—",
          pan_number:      p.pan_number || "",
          aadhar_no:       p.aadhar_no || "",
          profile_img:     p.profile_img || null,
          relation_hn:     p.relation_hn || "",
          // Father name — API returns father_name_en and father_name_hn
          father_name:     p.father_name || "",
          father_name_en:  p.father_name_en || "",
          father_name_hn:  p.father_name_hn || p.father_name || "",
          // Village — API now returns per_village_en and per_village_hn
          per_village:     p.per_village || "—",
          per_village_en:  p.per_village_en || "",
          per_village_hn:  p.per_village_hn || p.per_village || "",
          // Tahsil — API returns per_tahsil_en and per_tahsil_hn (tahsil not tehsil!)
          per_tahsil:      p.per_tahsil_hn || p.per_tahsil_en || "—",
          per_tahsil_en:   p.per_tahsil_en || "",
          per_tahsil_hn:   p.per_tahsil_hn || "",
          // District — API now returns per_district_en and per_district_hn
          per_district:    p.per_district || "—",
          per_district_en: p.per_district_en || "",
          per_district_hn: p.per_district_hn || p.per_district || "",
          per_state:       p.per_state || "—",
          per_pincode:     p.per_pincode || p.pincode || "—",
          joining_date:    p.joining_date || null,
          transactions:    [],
          totalPaid:       0,
        };
      }
      const amt = parseFloat(p.amount || p.net_amount || 0);
      map[id].transactions.push({
        id:                         p.id,
        receipt_no:                 p.receipt_no || "—",
        amount:                     amt,
        utr_number:                 p.utr_number || "—",
        transaction_date:           p.transaction_date,
        pyament_mode_type:          p.pyament_mode_type || "—",
        payment_virify:             p.payment_virify || p.last_payment_virify || "—",
        bank_account:               p.bank_account || "—",
        special_occassion_category: p.special_occassion_category || "—",
        remarks:                    p.remarks || "",
      });
      map[id].totalPaid += amt;
    });

    const minAmt = parseFloat(filters.amountMin) || 0;
    return Object.values(map)
      .filter(m => m.totalPaid >= minAmt)
      .sort((a, b) => b.totalPaid - a.totalPaid);
  };

  const handleReset = () => {
    setFilters({ txn_date_range: [], bank_account: "", pyament_mode_type: "", name: "", phone: "", amountMin: "", payment_virify: "" });
    setGroupedData([]);
    setGrandTotal(0);
    setAboveThresholdCount(0);
    setThreshold(DEFAULT_THRESHOLD);
  };

  // ── Excel Export ───────────────────────────────────────────────────────────
  const handleExcelDownload = () => {
    if (!groupedData.length) return;

    const dateLabel = filters.txn_date_range.length === 2
      ? `${filters.txn_date_range[0]} to ${filters.txn_date_range[1]}`
      : "All";

    const lv = (m, baseKey) => {
      if (language === "hi") return m[baseKey + "_hn"] || m[baseKey] || "";
      return m[baseKey + "_en"] || m[baseKey] || "";
    };

    // Sheet 1 – one row per member
    const summaryRows = groupedData.map((m, i) => {
      const row = {
        "S.No":           i + 1,
        "Name":           language === "hi" ? (m.name_hn || m.name) : (m.name_eng || m.name),
        "Father Name":    lv(m, "father_name"),
        "Phone":          m.phone_no,
        "PAN Number":     m.pan_number || (m.totalPaid >= threshold ? "MISSING - Required" : ""),
        "Aadhar No":      m.aadhar_no || "",
        "Village":        lv(m, "per_village"),
        "Tehsil":         lv(m, "per_tahsil"),
        "District":       lv(m, "per_district"),
        "State":          m.per_state,
        "Pincode":        m.per_pincode || "",
        "Joining Date":   m.joining_date ? moment(m.joining_date).format("DD-MM-YYYY") : "",
        "No. of Txns":    m.transactions.length,
        "Total Paid (₹)": m.totalPaid.toFixed(2),
        [`Above ₹${threshold.toLocaleString("en-IN")}`]: m.totalPaid >= threshold ? "YES" : "NO",
        "Period":         dateLabel,
      };
      if (visibleCols.pyament_mode_type) {
        row["Mode"] = txnUniqueVals(m, "pyament_mode_type");
      }
      return row;
    });

    // Sheet 2 – every individual transaction
    const detailRows = [];
    groupedData.forEach(m => {
      m.transactions.forEach(txn => {
        detailRows.push({
          "Name":           language === "hi" ? (m.name_hn || m.name) : (m.name_eng || m.name),
          "Father Name":    lv(m, "father_name"),
          "Phone":          m.phone_no,
          "PAN Number":     m.pan_number || "",
          "Village":        lv(m, "per_village"),
          "Tehsil":         lv(m, "per_tahsil"),
          "District":       lv(m, "per_district"),
          "Receipt No":     txn.receipt_no,
          "Amount (₹)":     txn.amount.toFixed(2),
          "Txn Date":       txn.transaction_date ? moment(txn.transaction_date).format("DD-MM-YYYY") : "—",
          "UTR / Txn ID":   txn.utr_number,
          "Mode":           txn.pyament_mode_type !== "—" ? txn.pyament_mode_type : "",
          "Status":         txn.payment_virify    !== "—" ? txn.payment_virify    : "",
          "Account":        txn.bank_account      !== "—" ? txn.bank_account      : "",
          "Occasion":       txn.special_occassion_category !== "—" ? txn.special_occassion_category : "",
          "Remarks":        txn.remarks || "",
          "Member Total":   m.totalPaid.toFixed(2),
          [`Above ₹${threshold.toLocaleString("en-IN")}`]: m.totalPaid >= threshold ? "YES" : "NO",
        });
      });
    });

    const wb  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), "Member Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows),  "All Transactions");

    const langSuffix = language === "hi" ? "HI" : "EN";
    const fname = filters.txn_date_range.length === 2
      ? `Txn-Summary-${filters.txn_date_range[0]}_to_${filters.txn_date_range[1]}-${langSuffix}.xlsx`
      : `Transaction-Summary-${langSuffix}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  const statusColor = (s) => {
    if (!s || s === "—") return {};
    if (s === "verified") return { background: "#52c41a", color: "#fff" };
    if (s === "failed")   return { background: "#ff4d4f", color: "#fff" };
    return { background: "#faad14", color: "#fff" };
  };

  // ── Expanded inner transaction table ──────────────────────────────────────
  const txnColumns = [
    { title: "Receipt No",  dataIndex: "receipt_no",        key: "receipt_no",   width: 130, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    {
      title: "Amount", dataIndex: "amount", key: "amount", width: 120,
      render: v => <span style={{ fontWeight: 700, color: "#52c41a" }}>₹{Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>,
    },
    {
      title: "Txn Date", dataIndex: "transaction_date", key: "transaction_date", width: 110,
      render: v => <span style={{ fontSize: 12 }}>{v ? moment(v).format("DD MMM YYYY") : "—"}</span>,
    },
    { title: "UTR / Txn ID", dataIndex: "utr_number",       key: "utr_number",   width: 180, render: v => <span style={{ fontSize: 11, color: "#555" }}>{v}</span> },
    { title: "Mode",          dataIndex: "pyament_mode_type", key: "mode",        width: 110, render: v => <Tag color="blue"  style={{ fontSize: 11 }}>{v}</Tag> },
    {
      title: "Status", dataIndex: "payment_virify", key: "status", width: 90,
      render: v => v && v !== "—"
        ? <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, ...statusColor(v) }}>{v.charAt(0).toUpperCase() + v.slice(1)}</span>
        : <span style={{ color: "#ccc" }}>—</span>,
    },
    { title: "Account",  dataIndex: "bank_account",          key: "bank_account", width: 200, render: v => <span style={{ fontSize: 11 }}>{v}</span> },
    {
      title: "Occasion / Remarks", key: "occ", width: 180,
      render: (_, r) => <span style={{ fontSize: 11 }}>{r.special_occassion_category !== "—" ? r.special_occassion_category : ""}{r.remarks ? ` — ${r.remarks}` : ""}</span>,
    },
  ];

  // ── Outer member columns (fixed + optional) ───────────────────────────────
  const baseMemberColumns = [
    {
      title: <span style={{ fontSize: 13, color: "#7a0000", fontWeight: 700 }}>S.No</span>,
      dataIndex: "sno", key: "sno", width: 55,
      render: v => <span style={{ fontWeight: 600, fontSize: 12, color: "#7a0000" }}>{v}</span>,
    },
    {
      title: <span style={{ fontSize: 13, color: "#7a0000", fontWeight: 700 }}>Member</span>,
      key: "member", width: 240,
      render: (_, r) => {
        const displayName   = language === "hi" ? (r.name_hn || r.name) : (r.name_eng || r.name);
        const displayFather = language === "hi" ? (r.father_name_hn || r.father_name) : (r.father_name_en || r.father_name);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar shape="square" size={34} src={r.profile_img || userDummy} icon={!r.profile_img && <UserOutlined />} style={{ borderRadius: 4, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                <Link to={`/users/view/${r.mamber_id}`} style={{ color: "#1890ff" }}>{displayName}</Link>
              </div>
              {r.relation_hn && displayFather && <div style={{ fontSize: 11, color: "#888" }}>{r.relation_hn} {displayFather}</div>}
            </div>
          </div>
        );
      },
    },
    {
      title: <span style={{ fontSize: 13, color: "#7a0000", fontWeight: 700 }}>Phone</span>,
      dataIndex: "phone_no", key: "phone_no", width: 130,
      render: v => <span style={{ fontSize: 13 }}>{v}</span>,
    },
    {
      title: <span style={{ fontSize: 13, color: "#7a0000", fontWeight: 700 }}>Txns</span>,
      key: "txn_count", width: 70,
      render: (_, r) => <Tag color="geekblue" style={{ fontSize: 12, fontWeight: 600 }}>{r.transactions.length}</Tag>,
    },
    {
      title: <span style={{ fontSize: 13, color: "#7a0000", fontWeight: 700 }}>Total Paid</span>,
      key: "totalPaid", width: 140,
      render: (_, r) => (
        <span style={{ fontWeight: 700, fontSize: 14, color: r.totalPaid >= threshold ? "#cf1322" : "#389e0d" }}>
          ₹{r.totalPaid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: <span style={{ fontSize: 13, color: "#7a0000", fontWeight: 700 }}>PAN</span>,
      key: "pan", width: 150,
      render: (_, r) => {
        if (r.totalPaid < threshold) return <span style={{ color: "#ccc" }}>—</span>;
        if (r.pan_number) return <Tag color="green" style={{ fontWeight: 700, fontSize: 12 }}>{r.pan_number}</Tag>;
        return (
          <Tooltip title="PAN required for amounts ≥ ₹50,000">
            <Tag color="red" icon={<WarningOutlined />} style={{ fontWeight: 600, fontSize: 11 }}>PAN Missing</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: <span style={{ fontSize: 13, color: "#7a0000", fontWeight: 700 }}>Flag</span>,
      key: "flag", width: 100,
      render: (_, r) => r.totalPaid >= threshold ? <Tag color="volcano" style={{ fontWeight: 700 }}>≥ ₹{threshold.toLocaleString("en-IN")}</Tag> : null,
    },
  ];

  const optionalMemberColumns = OPTIONAL_COLUMNS
    .filter(c => visibleCols[c.key])
    .map(c => ({
      title: <span style={{ fontSize: 13, color: "#7a0000", fontWeight: 700 }}>{c.label}</span>,
      key: c.key,
      width: 140,
      render: (_, r) => {
        if (c.key === "joining_date")
          return <span style={{ fontSize: 12 }}>{r.joining_date ? moment(r.joining_date).format("DD MMM YYYY") : "—"}</span>;
        if (c.key === "payment_virify") {
          const v = txnUniqueVals(r, "payment_virify");
          return v !== "—"
            ? <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, ...statusColor(v) }}>{v.charAt(0).toUpperCase() + v.slice(1)}</span>
            : <span style={{ color: "#ccc" }}>—</span>;
        }
        if (c.txnLevel)    return <span style={{ fontSize: 12 }}>{txnUniqueVals(r, c.key)}</span>;
        if (c.langVariant) return <span style={{ fontSize: 12 }}>{langVal(r, c.key)}</span>;
        return <span style={{ fontSize: 12 }}>{r[c.key] || "—"}</span>;
      },
    }));

  const memberColumns = [...baseMemberColumns, ...optionalMemberColumns];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={<span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Transaction Summary Report</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
          >
            <div style={{ padding: "16px 16px 0" }}>

              {/* ── Filters Row 1 ── */}
              <Row gutter={[12, 12]} align="bottom">
                <Col xs={24} md={7}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#555" }}>
                    Transaction Date Range <span style={{ color: "red" }}>*</span>
                  </div>
                  <RangePicker
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    value={
                      filters.txn_date_range.length === 2
                        ? [moment(filters.txn_date_range[0], "DD-MM-YYYY"), moment(filters.txn_date_range[1], "DD-MM-YYYY")]
                        : []
                    }
                    onChange={(dates, dateStrings) => handleFilterChange("txn_date_range", dates ? dateStrings : [])}
                    placeholder={["Start Txn Date", "End Txn Date"]}
                  />
                </Col>
                <Col xs={24} md={4}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#555" }}>Account</div>
                  <Select style={{ width: "100%" }} placeholder="All Accounts" value={filters.bank_account || undefined} allowClear onChange={v => handleFilterChange("bank_account", v || "")}>
                    {bankAccounts.map(acc => <Option key={acc.id} value={String(acc.id)}>{acc.account_name}</Option>)}
                  </Select>
                </Col>
                <Col xs={24} md={3}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#555" }}>Mode of Payment</div>
                  <Select style={{ width: "100%" }} placeholder="All Modes" value={filters.pyament_mode_type || undefined} allowClear onChange={v => handleFilterChange("pyament_mode_type", v || "")}>
                    {PAYMENT_MODES.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
                  </Select>
                </Col>
                <Col xs={24} md={3}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#555" }}>Name</div>
                  <Input placeholder="Name" value={filters.name} onChange={e => handleFilterChange("name", e.target.value)} />
                </Col>
                <Col xs={24} md={3}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#555" }}>Phone</div>
                  <Input placeholder="Phone" value={filters.phone} onChange={e => handleFilterChange("phone", e.target.value)} />
                </Col>
                <Col xs={24} md={2}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#555" }}>Min Total (₹)</div>
                  <Input placeholder="e.g. 50000" value={filters.amountMin} onChange={e => handleFilterChange("amountMin", e.target.value)} />
                </Col>
                <Col xs={24} md={2}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#555" }}>Status</div>
                  <Select style={{ width: "100%" }} placeholder="All" value={filters.payment_virify || undefined} allowClear onChange={v => handleFilterChange("payment_virify", v || "")}>
                    <Option value="verified">Verified</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="failed">Failed</Option>
                  </Select>
                </Col>
              </Row>

              {/* ── Filters Row 2: Threshold + Language ── */}
              <Row gutter={[12, 12]} align="bottom" style={{ marginTop: 10 }}>
                <Col xs={24} md={3}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#c41d7f" }}>
                    Flag Threshold (₹)
                  </div>
                  <Input
                    placeholder="e.g. 50000"
                    value={threshold}
                    onChange={e => setThreshold(parseFloat(e.target.value) || 0)}
                    style={{ borderColor: "#eb2f96" }}
                  />
                </Col>
                <Col xs={24} md={4}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#7a0000" }}>
                    Report Language
                  </div>
                  <Select style={{ width: "100%" }} value={language} onChange={v => setLanguage(v)}>
                    <Option value="en">English (अंग्रेज़ी)</Option>
                    <Option value="hi">Hindi (हिंदी)</Option>
                  </Select>
                </Col>
              </Row>

              {/* ── Column Selector ── */}
              <div style={{ marginTop: 14, padding: "10px 14px", background: "#f5f5f5", borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#7a0000", marginBottom: 8 }}>Show Extra Columns:</div>
                <Row gutter={[0, 4]}>
                  {OPTIONAL_COLUMNS.map(c => (
                    <Col key={c.key} style={{ marginRight: 18, marginBottom: 4 }}>
                      <Checkbox checked={visibleCols[c.key]} onChange={e => toggleCol(c.key, e.target.checked)}>
                        <span style={{ fontSize: 12 }}>{c.label}</span>
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* ── Action Buttons ── */}
              <Row gutter={[12, 0]} style={{ marginTop: 12, marginBottom: 16 }}>
                <Col>
                  <Button type="primary" icon={<SearchOutlined />} onClick={fetchReport} loading={loading} style={{ background: "#7a0000", borderColor: "#7a0000" }}>
                    Generate Report
                  </Button>
                </Col>
                <Col><Button onClick={handleReset}>Reset</Button></Col>
                {groupedData.length > 0 && (
                  <Col>
                    <Button icon={<FileExcelOutlined />} onClick={handleExcelDownload} style={{ background: "#217346", borderColor: "#217346", color: "#fff" }}>
                      Excel Download ({language === "hi" ? "हिंदी" : "English"})
                    </Button>
                  </Col>
                )}
              </Row>

              {/* ── Summary Stats ── */}
              {groupedData.length > 0 && (
                <Row gutter={[12, 8]} style={{ marginBottom: 16 }}>
                  <Col>
                    <div style={{ background: "#f0f5ff", border: "1px solid #d6e4ff", borderRadius: 8, padding: "10px 20px" }}>
                      <Text style={{ fontSize: 13 }}>Total Members: <strong>{groupedData.length}</strong></Text>
                    </div>
                  </Col>
                  <Col>
                    <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 8, padding: "10px 20px" }}>
                      <Text style={{ fontSize: 13 }}>Grand Total: <strong style={{ color: "#389e0d" }}>₹{grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></Text>
                    </div>
                  </Col>
                  <Col>
                    <div style={{ background: "#fff2f0", border: "1px solid #ffccc7", borderRadius: 8, padding: "10px 20px" }}>
                      <Text style={{ fontSize: 13 }}>Members ≥ ₹{threshold.toLocaleString("en-IN")}: <strong style={{ color: "#cf1322" }}>{aboveThresholdCount}</strong> <span style={{ color: "#888", fontSize: 11 }}>(flagged)</span></Text>
                    </div>
                  </Col>
                  <Col>
                    <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 8, padding: "10px 20px" }}>
                      <Text style={{ fontSize: 13 }}>
                        Excel has <strong>2 sheets</strong>: Member Summary + All Transactions
                      </Text>
                    </div>
                  </Col>
                </Row>
              )}
            </div>

            {/* ── Main Table ── */}
            <Spin spinning={loading}>
              <div className="table-responsive">
                <Table
                  columns={memberColumns}
                  dataSource={groupedData.map((m, i) => ({ ...m, key: m.mamber_id || m.phone_no || i, sno: i + 1 }))}
                  bordered
                  scroll={{ x: "max-content" }}
                  pagination={{ pageSize: 30, showTotal: t => `Total ${t} members`, style: { marginRight: 20 } }}
                  expandable={{
                    expandedRowRender: record => (
                      <div style={{ background: "#fafafa", padding: "8px 16px" }}>
                        <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 12, color: "#7a0000" }}>
                          All transactions for <strong>{record.name}</strong> within selected period:
                        </div>
                        <Table
                          columns={txnColumns}
                          dataSource={record.transactions.map((t, i) => ({ ...t, key: t.id || i }))}
                          pagination={false}
                          size="small"
                          bordered
                          scroll={{ x: "max-content" }}
                          summary={() => (
                            <Table.Summary.Row>
                              <Table.Summary.Cell index={0} colSpan={2}><strong>Sub-Total</strong></Table.Summary.Cell>
                              <Table.Summary.Cell index={2} colSpan={6}>
                                <strong style={{ color: "#52c41a", fontSize: 13 }}>
                                  ₹{record.totalPaid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </strong>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          )}
                        />
                      </div>
                    ),
                    rowExpandable: record => record.transactions.length > 0,
                  }}
                  rowClassName={record => record.totalPaid >= threshold ? "txn-row-highlight" : ""}
                  className="ant-border-space"
                />
              </div>
            </Spin>

            <style>{`
              .txn-row-highlight > td { background: #fff7f7 !important; }
              .txn-row-highlight:hover > td { background: #ffecec !important; }
            `}</style>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default TransactionSummaryReport;
